import { useEffect, useMemo, useState } from 'react';
import { Check, Copy, X } from 'lucide-react';
import { Evidence, EvidenceNode } from '../types';

type Tab = 'nodes' | 'relationships' | 'cypher';

interface Props {
  evidence: Evidence;
  onClose?: () => void;
  highlightNodeId?: string | null;
}

// Pull a few human-friendly identifiers out of node properties — element_id
// must not be the primary label per §7 of the spec.
function readableNodeId(node: EvidenceNode): string {
  const props = node.properties || {};
  const candidates = [
    'name',
    'companyName',
    'company_name',
    'title',
    'label',
    'report_id',
    'reportId',
    'companyId',
    'company_id',
    'fiscal_year',
    'fiscalYear',
    'id',
  ];
  for (const key of candidates) {
    if (props[key] !== undefined && props[key] !== null && props[key] !== '') {
      return String(props[key]);
    }
  }
  return node.labels[0] || 'Node';
}

function NodeRow({
  node,
  highlighted,
}: {
  node: EvidenceNode;
  highlighted: boolean;
}) {
  const [open, setOpen] = useState(highlighted);
  const props = node.properties || {};
  const keys = Object.keys(props);

  return (
    <div
      className={`border rounded-lg ${
        highlighted ? 'border-primary-400 bg-primary-50/40' : 'border-neutral-200'
      }`}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-neutral-50"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-mono bg-neutral-100 text-neutral-700">
            {node.labels.join(':') || 'Node'}
          </span>
          <span className="text-sm text-neutral-900 truncate">{readableNodeId(node)}</span>
        </div>
        <span className="text-xs text-neutral-500 flex-shrink-0">
          {keys.length} {keys.length === 1 ? 'prop' : 'props'}
        </span>
      </button>
      {open && (
        <dl className="px-3 pb-3 grid grid-cols-[max-content,1fr] gap-x-3 gap-y-1 text-xs">
          {keys.length === 0 ? (
            <div className="col-span-2 text-neutral-500 italic">No properties</div>
          ) : (
            keys.map((k) => (
              <div key={k} className="contents">
                <dt className="font-semibold text-neutral-600">{k}</dt>
                <dd className="text-neutral-800 font-mono break-all">
                  {typeof props[k] === 'object'
                    ? JSON.stringify(props[k])
                    : String(props[k])}
                </dd>
              </div>
            ))
          )}
        </dl>
      )}
    </div>
  );
}

export default function EvidencePanel({ evidence, onClose, highlightNodeId }: Props) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!evidence.cypher) return;
    try {
      await navigator.clipboard.writeText(evidence.cypher);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard API can fail under permissions — silently ignore
    }
  };

  // Only surface tabs whose data is actually present. Scalar-projection
  // responses (Recipe B1/E/F) return empty `nodes` + `relationships` but a
  // runnable `cypher` — the Cypher tab must still appear and be selectable.
  const availableTabs = useMemo<Tab[]>(() => {
    const out: Tab[] = [];
    if (evidence.nodes.length > 0) out.push('nodes');
    if (evidence.relationships.length > 0) out.push('relationships');
    if (evidence.cypher) out.push('cypher');
    return out;
  }, [evidence]);

  const [tab, setTab] = useState<Tab>(availableTabs[0] ?? 'cypher');

  // Keep the selected tab valid when the underlying evidence changes (e.g.
  // user switches between messages while the panel is open).
  useEffect(() => {
    if (availableTabs.length === 0) return;
    if (!availableTabs.includes(tab)) {
      setTab(availableTabs[0]);
    }
  }, [availableTabs, tab]);

  const noEvidence = availableTabs.length === 0;

  const TAB_LABELS: Record<Tab, string> = {
    nodes: `Nodes (${evidence.nodes.length})`,
    relationships: `Relationships (${evidence.relationships.length})`,
    cypher: 'Cypher',
  };

  return (
    <aside className="h-full w-full min-w-0 flex flex-col bg-white border-l border-neutral-200">
      <header className="flex-shrink-0 flex items-start justify-between gap-3 px-4 py-3 border-b border-neutral-200">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-neutral-900">Evidence</h2>
          {evidence.derivation && (
            <code className="block mt-1 text-[11px] font-mono text-neutral-500 break-all whitespace-pre-wrap line-clamp-3">
              {evidence.derivation}
            </code>
          )}
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-neutral-100 text-neutral-500 hover:text-neutral-700 flex-shrink-0"
            aria-label="Close evidence panel"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </header>

      {availableTabs.length > 0 && (
        <nav className="flex-shrink-0 flex border-b border-neutral-200 text-sm bg-white" role="tablist">
          {availableTabs.map((id) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              onClick={() => setTab(id)}
              className={`flex-1 px-3 py-2 font-medium transition-colors ${
                tab === id
                  ? 'text-primary-700 border-b-2 border-primary-600 -mb-px'
                  : 'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50'
              }`}
            >
              {TAB_LABELS[id]}
            </button>
          ))}
        </nav>
      )}

      <div className="flex-1 min-h-0 min-w-0 overflow-y-auto p-3">
        {noEvidence && (
          <div className="text-sm text-neutral-500 italic px-2 py-4 text-center">
            No evidence returned for this message.
          </div>
        )}

        {tab === 'nodes' && evidence.nodes.length > 0 && (
          <div className="space-y-2">
            {evidence.nodes.map((n) => (
              <NodeRow
                key={n.id}
                node={n}
                highlighted={highlightNodeId === n.id}
              />
            ))}
          </div>
        )}

        {tab === 'relationships' && evidence.relationships.length > 0 && (
          <div className="space-y-2">
            {evidence.relationships.map((r) => (
              <div
                key={r.id}
                className="border border-neutral-200 rounded-lg px-3 py-2 text-xs"
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-md font-mono bg-purple-50 text-purple-700">
                    {r.type}
                  </span>
                </div>
                <div className="font-mono text-neutral-700 break-all">
                  {r.start_node_id} → {r.end_node_id}
                </div>
                {Object.keys(r.properties || {}).length > 0 && (
                  <dl className="mt-2 grid grid-cols-[max-content,1fr] gap-x-3 gap-y-1">
                    {Object.entries(r.properties).map(([k, v]) => (
                      <div key={k} className="contents">
                        <dt className="font-semibold text-neutral-600">{k}</dt>
                        <dd className="text-neutral-800 font-mono break-all">
                          {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                        </dd>
                      </div>
                    ))}
                  </dl>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === 'cypher' && (
          <div className="relative min-w-0">
            {evidence.cypher ? (
              <>
                <button
                  type="button"
                  onClick={handleCopy}
                  className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-neutral-800 text-neutral-100 hover:bg-neutral-700"
                  aria-label="Copy Cypher to clipboard"
                >
                  {copied ? (
                    <>
                      <Check className="w-3 h-3" /> Copied
                    </>
                  ) : (
                    <>
                      <Copy className="w-3 h-3" /> Copy
                    </>
                  )}
                </button>
                <pre className="bg-neutral-900 text-neutral-100 text-xs font-mono p-3 pr-14 rounded-lg max-w-full overflow-x-auto whitespace-pre-wrap break-words">
                  {evidence.cypher}
                </pre>
              </>
            ) : (
              <div className="text-sm text-neutral-500 italic">
                No Cypher query was executed for this message.
              </div>
            )}
          </div>
        )}
      </div>
    </aside>
  );
}
